///
/// EarthBrowser
///
/// Created by Matt Giger on December 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { SatelliteNode } from "@/earthbrowser/dataset-distribution/satellites/SatelliteNode"
import { SatelliteGroupsTable, type SatelliteGroupProperties } from "@/earthbrowser/dataset-distribution/satellites/SatelliteGroupsTable"
import { cDefaultSatelliteSets, groupIdsForSets } from "@/earthbrowser/dataset-distribution/satellites/satelliteSets"
import { SatelliteTLETable, type SatelliteTLEProperties } from "@/earthbrowser/dataset-distribution/satellites/SatelliteTLETable"
import { type SatelliteEntry } from "@shared-types/satellite-entry"
import { DatasetLayer, type DatasetSceneObject, type DatasetFeature, type DatasetManifest, type DatasetSyncConfig } from "../DatasetLayer"

/** Raw Arrow row shape for the TLE feed (only fields we read) */
interface SatelliteTLERow {
	object_id: unknown
	object_name: unknown
	epoch: unknown
	mean_motion: unknown
	eccentricity: unknown
	inclination: unknown
	ra_of_asc_node: unknown
	arg_of_pericenter: unknown
	mean_anomaly: unknown
	norad_cat_id: unknown
	rev_at_epoch: unknown
	bstar: unknown
	mean_motion_dot: unknown
	mean_motion_ddot: unknown
	group_ids: string | string[] | null | undefined
}

/** Raw Arrow row shape for the satellite groups feed (only fields we read) */
interface SatelliteGroupRow {
	id: unknown
	name: unknown
	url: unknown
	active: unknown
	daily_load: unknown
	satellite_count: unknown
}

export interface SatelliteFeature extends SatelliteEntry, DatasetFeature {
	id: string
	object_id: string
	object_name: string
	epoch: string
	mean_motion: number
	eccentricity: number
	inclination: number
	ra_of_asc_node: number
	arg_of_pericenter: number
	mean_anomaly: number
	norad_cat_id: number
	rev_at_epoch: number
	bstar: number
	mean_motion_dot: number
	mean_motion_ddot: number
	/** CelesTrak group ids the object belongs to; drives its marker archetype */
	group_ids: string[]
}

export class SatelliteDatasetLayer extends DatasetLayer<SatelliteFeature> {
	// Selected set ids (satelliteSets.ts); the globe shows their union
	private selectedSets: readonly string[] = cDefaultSatelliteSets
	private unsubscribeRedux?: () => void

	// Satellites loaded on demand (search/deep-link) that must survive set refreshes
	private pinnedIds = new Set<string>()

	constructor(context: EarthBrowserContext) {
		super(context, 'satellites')

		const state = engineBridge.getState()?.earthbrowser
		this.selectedSets = state?.satelliteSets ?? this.selectedSets
		this.visible = state?.visibleDatasets.includes("satellite") ?? false

		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const newState = engineBridge.getState()?.earthbrowser
			if (!newState) return

			const newSets = newState.satelliteSets
			if (newSets !== this.selectedSets && !sameStrings(newSets, this.selectedSets)) {
				this.selectedSets = newSets
				void this.refreshFromSets()
			}

			const isVisible = newState.visibleDatasets.includes("satellite")
			if (isVisible !== this.visible) {
				this.visible = isVisible
			}
		})
	}

	// ==================== Sync Configuration ====================

	/**
	 * Return sync config for satellite_tle dataset.
	 * Note: satellite_groups is also synced as a sub-dataset.
	 */
	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "satellite_tle",
			pollIntervalMs: 0, // No polling needed for satellites
			needsSync: true,
			supportsDelta: false,
		}
	}

	/**
	 * Return sub-datasets that need to be synced
	 */
	public getSubDatasetIds(): readonly string[] {
		return ["satellite_groups"]
	}

	/**
	 * Apply checkpoint for sub-datasets
	 */
	public async applySubDatasetCheckpoint(
		datasetId: string,
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		if (datasetId === "satellite_groups") {
			await this.applySatelliteGroupsCheckpoint(manifest, downloadArrowRows)
		}
	}

	public async createTable(db: Database): Promise<void> {
		await SatelliteGroupsTable.create(db)
		await SatelliteTLETable.create(db)
	}

	public override async getStoredRowCount(datasetId: string): Promise<number | null> {
		if (datasetId === "satellite_tle") return SatelliteTLETable.getCount()
		if (datasetId === "satellite_groups") return SatelliteGroupsTable.getCount()
		return null
	}

	/**
	 * Apply checkpoint for satellite_tle.
	 * Note: satellite_groups is synced separately via syncSecondaryDataset.
	 */
	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[SatelliteDatasetLayer] Downloading TLE shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[SatelliteDatasetLayer] Parsed ${rows.length} TLE rows`)

		const satellites = this.parseTLERows(rows)
		await SatelliteTLETable.replaceAll(satellites)
	}

	/**
	 * Apply satellite_groups checkpoint (secondary dataset)
	 */
	public async applySatelliteGroupsCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[SatelliteDatasetLayer] Downloading groups shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[SatelliteDatasetLayer] Parsed ${rows.length} groups rows`)

		const groups = this.parseGroupRows(rows)
		await SatelliteGroupsTable.replaceAll(groups)
	}

	public async onSyncComplete(): Promise<void> {
		await this.refreshFromSets()
	}

	/**
	 * Override refresh to load satellites from the selected sets
	 */
	public override async refresh(): Promise<void> {
		await this.refreshFromSets()
	}

	private parseTLERows(rows: unknown[]): SatelliteTLEProperties[] {
		return (rows as SatelliteTLERow[]).map(r => {
			let groupIds: string[] = []
			if (r.group_ids) {
				try {
					groupIds = typeof r.group_ids === 'string' ? JSON.parse(r.group_ids) as string[] : r.group_ids
				} catch {
					groupIds = []
				}
			}

			return {
				id: String(r.object_id),
				object_id: String(r.object_id),
				object_name: String(r.object_name),
				epoch: String(r.epoch),
				mean_motion: Number(r.mean_motion),
				eccentricity: Number(r.eccentricity),
				inclination: Number(r.inclination),
				ra_of_asc_node: Number(r.ra_of_asc_node),
				arg_of_pericenter: Number(r.arg_of_pericenter),
				mean_anomaly: Number(r.mean_anomaly),
				norad_cat_id: Number(r.norad_cat_id),
				rev_at_epoch: Number(r.rev_at_epoch),
				bstar: Number(r.bstar),
				mean_motion_dot: Number(r.mean_motion_dot),
				mean_motion_ddot: Number(r.mean_motion_ddot),
				group_ids: groupIds,
			}
		})
	}

	private parseGroupRows(rows: unknown[]): SatelliteGroupProperties[] {
		return (rows as SatelliteGroupRow[]).map(r => ({
			id: String(r.id),
			name: String(r.name),
			url: String(r.url),
			active: Boolean(r.active),
			daily_load: Boolean(r.daily_load),
			satellite_count: Number(r.satellite_count ?? 0),
		}))
	}

	// ==================== Scene Objects ====================

	public createSceneObject(feature: SatelliteFeature): DatasetSceneObject<SatelliteFeature> | undefined {
		return new SatelliteNode(this.context, feature, this)
	}

	/**
	 * Refresh satellites from the selected sets (union of their CelesTrak
	 * groups), plus any pinned (on-demand loaded) ones
	 */
	private async refreshFromSets(): Promise<void> {
		try {
			const satellites = await SatelliteTLETable.getByGroupIds(groupIdsForSets(this.selectedSets))

			// Pinned satellites (focused via search/deep-link) survive set changes
			const pinned = await SatelliteTLETable.getByIds([...this.pinnedIds])

			const merged = new Map<string, SatelliteTLEProperties>()
			for (const sat of [...satellites, ...pinned]) {
				merged.set(sat.object_id, sat)
			}

			this.updateSceneObjects([...merged.values()].map(sat => this.toFeature(sat)))
		} catch (error) {
			console.error(`[SatelliteDatasetLayer] Error refreshing satellites:`, error)
		}
	}

	private toFeature(sat: SatelliteTLEProperties): SatelliteFeature {
		return {
			id: sat.object_id,
			object_id: sat.object_id,
			object_name: sat.object_name,
			epoch: sat.epoch,
			mean_motion: sat.mean_motion,
			eccentricity: sat.eccentricity,
			inclination: sat.inclination,
			ra_of_asc_node: sat.ra_of_asc_node,
			arg_of_pericenter: sat.arg_of_pericenter,
			mean_anomaly: sat.mean_anomaly,
			norad_cat_id: sat.norad_cat_id,
			rev_at_epoch: sat.rev_at_epoch,
			bstar: sat.bstar,
			mean_motion_dot: sat.mean_motion_dot,
			mean_motion_ddot: sat.mean_motion_ddot,
			group_ids: sat.group_ids ?? [],
		}
	}

	/**
	 * Load a satellite on demand (global search, history deep-links) even when
	 * it isn't in a selected set. Loaded satellites are pinned so they
	 * survive subsequent set refreshes.
	 */
	override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<SatelliteFeature> | undefined> {
		const existing = this.getSceneObject(id)
		if (existing) {
			return existing
		}

		const rows = await SatelliteTLETable.getByIds([id])
		const sat = rows[0]
		if (!sat) {
			console.warn(`[SatelliteDatasetLayer] Satellite not found: ${id}`)
			return undefined
		}

		this.pinnedIds.add(id)
		this.addSceneObject(this.toFeature(sat))
		return this.sceneObjects.get(id)
	}

	/**
	 * Override dispose to clean up the Redux subscription
	 */
	override destroy(): void {
		if (this.unsubscribeRedux) {
			this.unsubscribeRedux()
		}
		super.destroy()
	}
}

function sameStrings(a: readonly string[], b: readonly string[]): boolean {
	return a.length === b.length && a.every((v, i) => v === b[i])
}
